import React, { useEffect, useRef, useState } from 'react';
import { Table } from 'reactstrap';
import { LuArrowDown, LuArrowUp, LuArrowUpDown } from "react-icons/lu";
import { USERS_COLUMNS, columnWidthStyle } from './usersColumns';
import { isMobile } from '../../../utils/deviceInfo';

// 14px sits with the 13px/600 header label; the old arrow was 22px.
const SORT_ICON_SIZE = 14;

// A cell can't render narrower than its own 15px + 15px padding. Dragging a
// column below that pushed the row past 100% and gave the header a scrollbar.
const CELL_PADDING_PX = 30;

const UsersTableHeader = ({ updateBy, role, columnWidths, onColumnResize, onColumnResizeEnd }) => {
  const [sortState, setSortState] = useState({
    field: '',
    order: ''
  });
  const headingRowRef = useRef(null);
  const stopResizeRef = useRef(null);
  const suppressSortClickRef = useRef(false);

  // Unmounting mid-drag must not leave the document listeners behind.
  useEffect(() => () => {
    if (stopResizeRef.current) stopResizeRef.current();
  }, []);

  const handleSortBy = (field) => {
    let newOrder;
    if (sortState.field === field && sortState.order === 'asc') {
      newOrder = 'desc';
    } else if (sortState.field === field && sortState.order === 'desc') {
      newOrder = 'default';
    } else {
      newOrder = 'asc';
    }

    if (newOrder === 'default') {
      setSortState({ field: '', order: '' });
      updateBy('default', '');
    } else {
      setSortState({ field, order: newOrder });
      updateBy(field, newOrder);
    }
  };

  // Drags the boundary between this column and the next one: what one gains the
  // other loses, so the row always adds up to 100% and nothing overflows.
  const startResize = (event, index) => {
    event.preventDefault();
    event.stopPropagation();
    const row = headingRowRef.current;
    if (!row || !onColumnResize) return;
    const rowWidth = row.getBoundingClientRect().width;
    if (!rowWidth) return;

    // Measured, not read from props, so the first drag starts from the CSS defaults.
    const startWidths = {};
    Array.from(row.children).forEach((cell, i) => {
      startWidths[USERS_COLUMNS[i].key] = (cell.getBoundingClientRect().width / rowWidth) * 100;
    });

    const key = USERS_COLUMNS[index].key;
    const nextKey = USERS_COLUMNS[index + 1].key;
    const pairWidth = startWidths[key] + startWidths[nextKey];
    const paddingWidth = (CELL_PADDING_PX / rowWidth) * 100;
    if (pairWidth < paddingWidth * 2) return;
    const startX = event.clientX;

    const handleMove = (moveEvent) => {
      const delta = ((moveEvent.clientX - startX) / rowWidth) * 100;
      // Neither column of the pair goes below an empty cell's padding.
      const width = Math.min(Math.max(startWidths[key] + delta, paddingWidth), pairWidth - paddingWidth);
      onColumnResize({ ...startWidths, [key]: width, [nextKey]: pairWidth - width });
    };

    const stopResize = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.body.classList.remove('users-col-resizing');
      stopResizeRef.current = null;
    };

    const handleUp = () => {
      stopResize();
      if (onColumnResizeEnd) onColumnResizeEnd();
      // Releasing over a heading fires its click, which would sort the table.
      suppressSortClickRef.current = true;
      setTimeout(() => {
        suppressSortClickRef.current = false;
      }, 0);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.body.classList.add('users-col-resizing');
    stopResizeRef.current = stopResize;
  };

  const renderSortArrow = (field) => {
    if (field === sortState.field) {
      if (sortState.order === 'asc') {
        return <LuArrowUp size={SORT_ICON_SIZE} className="users-th-sort-icon is-active" />;
      }
      return <LuArrowDown size={SORT_ICON_SIZE} className="users-th-sort-icon is-active" />;
    }
    return <LuArrowUpDown size={SORT_ICON_SIZE} className="users-th-sort-icon" />;
  };

  const getMobileHeading = () => {
    if (role === 'Admin') return 'Mobile Number';
    if (role === 'User' || role === 'Member') return '';
    return 'Phone Number';
  };

  const isAdmin = !['User', 'Member'].includes(role);

  const headingFor = (column) => {
    if (column.key === 'mobileNumber') {
      return getMobileHeading();
    }
    // The cells are Admin-only (like Mobile Number), so a non-admin gets no label over an empty column.
    if (column.key === 'employeeId' && !isAdmin) {
      return '';
    }
    return column.label;
  };

  // Mobile Number, Added Date and User ID keep Admin-only arrows (their cells are
  // Admin-only too); the rest follow the column model.
  const showSortArrow = (column) => {
    if (!column.sortable) return false;
    if (column.key === 'mobileNumber' || column.key === 'addedDate' || column.key === 'employeeId') return isAdmin;
    return true;
  };

  // The 3-dots column keeps its fixed share, so the Role/actions boundary has no handle.
  const isResizable = (index) => {
    const nextColumn = USERS_COLUMNS[index + 1];
    return !isMobile && USERS_COLUMNS[index].key !== 'actions' && nextColumn && nextColumn.key !== 'actions';
  };

  const handleHeadingClick = (column) => {
    if (suppressSortClickRef.current) return;
    handleSortBy(column.key);
  };

  return (
    <Table responsive className="users-table-heading-table">
      <thead>
        <tr className="users-table-heading-row" ref={headingRowRef}>
          {USERS_COLUMNS.map((column, index) => (
            <th
              key={column.key}
              className={`users-th users-th-${column.key}`}
              style={isMobile ? undefined : columnWidthStyle(columnWidths, column.key)}
              onClick={column.sortable ? () => handleHeadingClick(column) : undefined}
            >
              <span className="users-th-label">
                {headingFor(column)} {showSortArrow(column) && renderSortArrow(column.key)}
              </span>
              {isResizable(index) && (
                <span
                  className="users-th-resizer"
                  onMouseDown={(e) => startResize(e, index)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </th>
          ))}
        </tr>
      </thead>
    </Table>
  );
};

export default UsersTableHeader;
